// TODO: Theme and Monochrome Logo Set Up
// If Dark BG with Monochrome invert them to White
// If White BG with Monochrome keep them black

import CfSectionShell from '../cf-section-shell'
import { useRef } from 'react'
import { useGSAP } from '@gsap/react'
import { gsap } from 'gsap'
import type { CfImage } from '~/types/contentful'
import { contentfulImage } from '~/utils/contentful-image'
import type { CfSectionShellFields } from '../cf-section-shell'

export type Logo = {
  fields: {
    name: string
    logo: CfImage
    url?: string
    monochromeVariant?: CfImage | null
    priority?: number
  }
}

export type LogoStripFields = {
  headline: string
  variant: 'Default' | 'Scroll'
  logos: Logo
  useMonochromeLogos: boolean
}

interface CfLogoStripSectionProps {
  fields: CfSectionShellFields & LogoStripFields
}

const normalizeAssetUrl = (url?: string) =>
  contentfulImage(url, { w: 400 }) ?? ''

// GSAP
const horizontalLoop = (
  items_: string | object | Element | null,
  config: {
    repeat?: number
    paused?: boolean
    speed?: number
    snap?: number | false
    paddingRight?: number
    reversed?: boolean
  } = {},
) => {
  const items = gsap.utils.toArray(items_) as HTMLDivElement[]
  const tl = gsap.timeline({
    repeat: config.repeat,
    paused: config.paused,
    defaults: { ease: 'none' },
    onReverseComplete: () => {
      tl.totalTime(tl.rawTime() + tl.duration() * 100)
    },
  })

  const length = items.length
  const startX = items[0].offsetLeft
  const times: number[] = []
  const widths: number[] = []
  const xPercents: number[] = []
  let curIndex = 0
  const pixelsPerSecond = (config.speed || 1) * 100
  const snap =
    config.snap === false ? (v: number) => v : gsap.utils.snap(config.snap || 1)
  let curX
  let distanceToStart
  let distanceToLoop
  let item
  let i

  gsap.set(items, {
    xPercent: (i, el) => {
      const w = (widths[i] = parseFloat(
        gsap.getProperty(el, 'width', 'px') as string,
      ))
      xPercents[i] = snap(
        (parseFloat(gsap.getProperty(el, 'x', 'px') as string) / w) * 100 +
          (gsap.getProperty(el, 'xPercent') as number),
      )
      return xPercents[i]
    },
  })
  gsap.set(items, { x: 0 })

  const totalWidth =
    items[length - 1].offsetLeft +
    (xPercents[length - 1] / 100) * widths[length - 1] -
    startX +
    items[length - 1].offsetWidth *
      (gsap.getProperty(items[length - 1], 'scaleX') as number) +
    (config.paddingRight ?? 0)

  for (i = 0; i < length; i++) {
    item = items[i]
    curX = (xPercents[i] / 100) * widths[i]
    distanceToStart = item.offsetLeft + curX - startX
    distanceToLoop =
      distanceToStart + widths[i] * (gsap.getProperty(item, 'scaleX') as number)

    tl.to(
      item,
      {
        xPercent: snap(((curX - distanceToLoop) / widths[i]) * 100),
        duration: distanceToLoop / pixelsPerSecond,
      },
      0,
    )
      .fromTo(
        item,
        {
          xPercent: snap(
            ((curX - distanceToLoop + totalWidth) / widths[i]) * 100,
          ),
        },
        {
          xPercent: xPercents[i],
          duration:
            (curX - distanceToLoop + totalWidth - curX) / pixelsPerSecond,
          immediateRender: false,
        },
        distanceToLoop / pixelsPerSecond,
      )
      .add('label' + i, distanceToStart / pixelsPerSecond)

    times[i] = distanceToStart / pixelsPerSecond
  }

  function toIndex(index: number, vars: any = {}) {
    if (Math.abs(index - curIndex) > length / 2) {
      index += index > curIndex ? -length : length
    }
    const newIndex = gsap.utils.wrap(0, length, index)
    let time = times[newIndex]
    if (time > tl.time() !== index > curIndex) {
      vars.modifiers = { time: gsap.utils.wrap(0, tl.duration()) }
      time += tl.duration() * (index > curIndex ? 1 : -1)
    }
    curIndex = newIndex
    vars.overwrite = true
    return tl.tweenTo(time, vars)
  }

  tl.progress(1, true).progress(0, true)
  if (config.reversed) {
    tl.vars.onReverseComplete?.()
    tl.reverse()
  }

  return Object.assign(tl, {
    next: (vars: any) => toIndex(curIndex + 1, vars),
    previous: (vars: any) => toIndex(curIndex - 1, vars),
    current: () => curIndex,
    toIndex: (index: number, vars: any) => toIndex(index, vars),
    times,
  })
}
// End GSAP

export function CfLogoStripSection({ fields }: CfLogoStripSectionProps) {
  const { id, fullWidth, paddingTop, paddingBottom, showBottomBorder, theme } =
    fields
  const { headline, logos, useMonochromeLogos, variant } = fields
  const isScroll = variant === 'Scroll'

  //GSAP
  const marquee = useRef<HTMLDivElement>(null)
  const mm = gsap.matchMedia()

  useGSAP(
    () => {
      if (isScroll) {
        mm.add('(min-width: 768px)', () => {
          horizontalLoop(marquee.current?.childNodes ?? [], {
            speed: 0.3,
            reversed: false,
            repeat: -1,
          })
        })
      }
    },
    { scope: marquee },
  )

  return (
    <CfSectionShell
      id={id}
      paddingTop={paddingTop}
      paddingBottom={paddingBottom}
      showBottomBorder={showBottomBorder}
      theme={theme}
      fullWidth={fullWidth}
    >
      <div className={`marquee ${isScroll ? 'full-width' : ''}`}>
        {headline ? (
          <hgroup className="marquee--title">
            <span>{headline}</span>
          </hgroup>
        ) : null}
        <div ref={marquee} className="marquee--inner">
          <div className={`marquee--content ${!isScroll ? 'static' : ''}`}>
            {Array.isArray(logos) &&
              logos
                .sort((a: Logo, b: Logo) => {
                  const p1 = Number(a.fields.priority ?? 99)
                  const p2 = Number(b.fields.priority ?? 99)
                  return p1 - p2
                })
                .map((logo) => {
                  const logoImage =
                    useMonochromeLogos && logo.fields.monochromeVariant?.fields
                      ? logo.fields.monochromeVariant?.fields
                      : logo.fields.logo?.fields
                  const file = logoImage?.file
                  const imageDetails = file?.details?.image

                  const imgWidth = imageDetails?.width ?? 1
                  const imgHeight = imageDetails?.height ?? 1
                  const imgUrl = normalizeAssetUrl(file?.url)

                  if (!imgUrl) return null

                  return (
                    <div
                      className="marquee--logo-mobile-wrapper"
                      key={logo.sys.id}
                    >
                      <div
                        style={
                          {
                            '--width': imgWidth,
                            '--height': imgHeight,
                          } as React.CSSProperties
                        }
                        className="logo-image"
                      >
                        <img src={imgUrl} alt={`${logo.fields.name} logo`} />
                      </div>
                    </div>
                  )
                })}
          </div>

          {isScroll && (
            <div
              className="marquee--content hide-for-mobile"
              aria-hidden="true"
            >
              {Array.isArray(logos) &&
                logos
                  .sort((a: Logo, b: Logo) => {
                    const p1 = Number(a.fields.priority ?? 99)
                    const p2 = Number(b.fields.priority ?? 99)
                    return p1 - p2
                  })
                  .map((logo) => {
                    const logoImage =
                      useMonochromeLogos &&
                      logo.fields.monochromeVariant?.fields
                        ? logo.fields.monochromeVariant?.fields
                        : logo.fields.logo?.fields
                    const file = logoImage?.file
                    const imageDetails = file?.details?.image

                    const imgWidth = imageDetails?.width ?? 1
                    const imgHeight = imageDetails?.height ?? 1
                    const imgUrl = normalizeAssetUrl(file?.url)

                    const logoName = logo.fields.name ?? 'logo'
                    if (!imgUrl) return null
                    return (
                      <div
                        className="marquee--logo-mobile-wrapper"
                        key={logo.sys.id}
                      >
                        <div
                          style={
                            {
                              '--width': imgWidth,
                              '--height': imgHeight,
                            } as React.CSSProperties
                          }
                          className="logo-image"
                        >
                          <img src={imgUrl} alt={`${logoName} logo`} />
                        </div>
                      </div>
                    )
                  })}
            </div>
          )}
        </div>
      </div>
    </CfSectionShell>
  )
}
